import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Minus } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SAMPLE_STEPS } from "../data/site";

const PATHS = [
  { name: "Common Library",      fee: "None / minimal",            lead: "Fastest",     unique: "Shared",      bestFor: "Speed-to-market, first orders, market tests" },
  { name: "Indus Art Exclusive",  fee: "Premium on unit cost",      lead: "Standard",    unique: "Not sold elsewhere", bestFor: "Brand differentiation, curated collections" },
  { name: "Client-Supplied Art", fee: "Separate design/setup fee", lead: "Longer (block carving + sampling)", unique: "100% yours", bestFor: "Private label, signature ranges, full IP control" },
];

export default function CustomOrders() {
  return (
    <main data-testid="custom-orders-page" className="pt-28">
      <section className="py-16 md:py-24 bg-[#faf6ef] border-b border-[#1a1a1a]/10">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase mb-4">Custom Orders</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] tracking-tight font-light">Three design paths. One workshop.</h1>
          <p className="mt-6 text-lg text-[#1a1a1a]/70 max-w-2xl mx-auto leading-[1.8]">Whether you want a fast-moving capsule, something no competitor has, or a signature range built from your artwork — we have a clear path for each.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-3 gap-px bg-[#1a1a1a]/10 border border-[#1a1a1a]/10">
          {PATHS.map((p, i) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-white p-8 md:p-10" data-testid={`path-${i}`}>
              <div className="text-[#d98f2f] font-serif text-4xl mb-4">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-serif text-2xl text-[#1e2a5a] mb-6">{p.name}</h3>
              <dl className="text-sm space-y-4">
                <Row l="Design fee" v={p.fee} />
                <Row l="Lead time" v={p.lead} />
                <Row l="Uniqueness" v={p.unique} />
                <Row l="Best for" v={p.bestFor} />
              </dl>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-[#1e2a5a] text-[#faf6ef]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="text-[#d98f2f] text-xs tracking-[0.3em] uppercase mb-4">Sampling Process</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] font-light mb-14 max-w-2xl">Five steps from your brief to a physical sample.</h2>
          <div className="grid md:grid-cols-5 gap-8">
            {SAMPLE_STEPS.map((s, i) => (
              <div key={s.n} data-testid={`co-sample-step-${i}`}>
                <div className="text-[#d98f2f] font-serif text-5xl mb-4">{s.n}</div>
                <div className="font-serif text-xl mb-2">{s.t}</div>
                <div className="text-sm text-[#faf6ef]/70 leading-[1.7]">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="lead-form" className="py-20 md:py-28 bg-[#faf6ef]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase mb-4">Start your custom order</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] font-light mb-6">Tell us about your project.</h2>
            <p className="text-[#1a1a1a]/75 leading-[1.8]">Share a rough brief. We'll come back with a recommended design path, indicative MOQ, a timeline, and the sample plan — within one business day.</p>
          </div>
          <div className="md:col-span-7">
            <LeadForm source="custom_orders" prefill={{ categories: ["Custom Design"] }} />
          </div>
        </div>
      </section>
    </main>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex items-start gap-3 border-t border-[#1a1a1a]/10 pt-3">
      <Check size={14} strokeWidth={1.5} className="text-[#8a9a7b] mt-[3px]" />
      <div className="flex-1">
        <dt className="text-[10px] tracking-[0.25em] uppercase text-[#1a1a1a]/50 mb-1">{l}</dt>
        <dd className="text-[#1a1a1a]">{v}</dd>
      </div>
    </div>
  );
}
